import React from "react";
import DataTable from "react-data-table-component";
import Checkbox from "@mui/material/Checkbox";
import { ArrowDownward } from "@mui/icons-material";
import { tokens } from "../../theme/tokens";
import { InputText } from "primereact/inputtext";
import { MultiSelect } from "primereact/multiselect";
import { Dropdown } from "primereact/dropdown";
import GradientCircularProgress from "./GradientCircularProgress";

const sortIcon = <ArrowDownward style={{ color: 'white' }} />;
const selectProps = { 
  indeterminate: (isIndeterminate) => isIndeterminate
};

// Custom filter input component with TrackNow styling
const FilterInput = ({ placeholder, value, onChange }) => {
  return (
    <InputText
      placeholder={placeholder}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      style={{
        width: '100%',
        fontSize: '12px',
        padding: '4px 8px',
        backgroundColor: tokens.colors.offWhite,
        border: `1px solid ${tokens.colors.grey}`,
        borderRadius: '4px'
      }}
    />
  );
};

// Custom multi-select filter with TrackNow styling
const FilterMultiSelect = ({ options, value, onChange, placeholder }) => {
  return (
    <MultiSelect
      value={value || []}
      options={options}
      onChange={(e) => onChange(e.value)}
      placeholder={placeholder}
      maxSelectedLabels={1}
      style={{
        width: '100%',
        fontSize: '12px',
        backgroundColor: tokens.colors.offWhite,
        border: `1px solid ${tokens.colors.grey}`,
        borderRadius: '4px'
      }}
    />
  );
};

// Custom dropdown filter with TrackNow styling
const FilterDropdown = ({ options, value, onChange, placeholder }) => {
  return (
    <Dropdown
      value={value}
      options={options}
      onChange={(e) => onChange(e.value)}
      placeholder={placeholder}
      style={{
        width: '100%',
        fontSize: '12px',
        backgroundColor: tokens.colors.offWhite,
        border: `1px solid ${tokens.colors.grey}`,
        borderRadius: '4px'
      }}
    />
  );
};

// Custom styles for react-data-table-component to match TrackNow design
const createCustomStyles = (tableHeaderColor = tokens.colors.connectedPurple) => ({
  table: {
    style: {
      backgroundColor: tokens.colors.offWhite,
      border: `1px solid ${tokens.colors.grey}`,
      borderRadius: '8px',
      overflow: 'hidden',
    },
  },
  header: {
    style: {
      backgroundColor: tokens.colors.offWhite,
      borderBottom: 'none',
      padding: '0',
    },
  },
  headRow: {
    style: {
      backgroundColor: tableHeaderColor,
      borderBottom: 'none',
      minHeight: '48px',
      position: 'sticky',
      top: 0,
      zIndex: 1,
    },
  },
  headCells: {
    style: {
      color: 'white !important',
      fontSize: '14px',
      fontWeight: '600',
      fontFamily: tokens.typography.fontFamily,
      borderRight: '1px solid rgba(255, 255, 255, 0.2)',
      backgroundColor: `${tableHeaderColor} !important`,
      '&:last-child': {
        borderRight: 'none',
      },
      '& svg': {
        color: 'white !important',
        opacity: '1 !important',
      },
    },
  },
  rows: {
    style: {
      backgroundColor: tokens.colors.offWhite,
      borderBottom: 'none',
      '&:hover': {
        backgroundColor: tokens.colors.midGrey + '20',
      },
    },
  },
  cells: {
    style: {
      borderRight: `1px solid ${tokens.colors.grey}`,
      borderBottom: 'none',
      fontSize: '14px',
      fontFamily: tokens.typography.fontFamily,
      color: tokens.colors.offBlack,
      '&:last-child': {
        borderRight: 'none',
      },
    },
  },
  pagination: {
    style: {
      backgroundColor: tokens.colors.offWhite,
      borderTop: `1px solid ${tokens.colors.grey}`,
    },
  },
  subHeader: {
    style: {
      backgroundColor: tokens.colors.offWhite,
      padding: '0',
    },
  },
  tableWrapper: {
    style: {
      backgroundColor: tokens.colors.offWhite,
    },
  },
});

function DataTableBase(props) {
  const { onScroll, fixedHeader, fixedHeaderScrollHeight, tableHeaderColor, ...restProps } = props;
  const wrapperRef = React.useRef(null);
  const scrollContainerRef = React.useRef(null);
  const customStyles = React.useMemo(
    () => createCustomStyles(tableHeaderColor),
    [tableHeaderColor]
  );

  React.useEffect(() => {
    if (!onScroll || !wrapperRef.current) return;

    const attachScrollHandler = () => {
      // Remove old listener if exists
      if (scrollContainerRef.current) {
        scrollContainerRef.current.removeEventListener('scroll', onScroll);
        scrollContainerRef.current = null;
      }

      // Find the scrollable div
      const allDivs = wrapperRef.current.querySelectorAll('div');
      for (let div of allDivs) {
        const style = window.getComputedStyle(div);
        if ((style.overflowY === 'auto' || style.overflowY === 'scroll') && div.scrollHeight > div.clientHeight) {
          console.log('✅ DataTableBase: Attaching scroll handler. scrollHeight:', div.scrollHeight, 'clientHeight:', div.clientHeight);
          div.addEventListener('scroll', onScroll, { passive: true });
          scrollContainerRef.current = div;
          return true;
        }
      }
      return false;
    };

    // Try multiple times with delay
    let attempts = 0;
    const maxAttempts = 10;
    const interval = setInterval(() => {
      const attached = attachScrollHandler();
      attempts++;
      if (attached) {
        console.log('✅ DataTableBase: Scroll handler attached successfully after', attempts, 'attempts');
        clearInterval(interval);
      } else if (attempts >= maxAttempts) {
        console.error('❌ DataTableBase: Could not find scrollable container after', attempts, 'attempts');
        clearInterval(interval);
      }
    }, 100);

    return () => {
      clearInterval(interval);
      if (scrollContainerRef.current) {
        scrollContainerRef.current.removeEventListener('scroll', onScroll);
      }
    };
  }, [onScroll, restProps.data?.length]);

  const selectAllCheckbox = React.useMemo(() => {
    return (
      <Checkbox
        sx={{
          color: 'white',
          '&.Mui-checked': {
            color: 'white',
          },
          '& .MuiSvgIcon-root': {
            color: 'white',
          }
        }}
      />
    );
  }, []);

  return (
    <div ref={wrapperRef}>
      <DataTable
        customStyles={customStyles}
        fixedHeader={fixedHeader}
        fixedHeaderScrollHeight={fixedHeaderScrollHeight}
        selectableRowsComponent={Checkbox}
        selectableRowsComponentProps={selectProps}
        selectableRowsHeader={selectAllCheckbox}
        sortIcon={sortIcon}
        dense
        progressComponent={<GradientCircularProgress />}
        {...restProps}
      />
    </div>
  );
}

export default DataTableBase;
